import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../components/contexts/AuthContext';
import axios from 'axios';
import { API_DOMAIN, API_ENDPOINTS } from '../components/constants';
import useSnackbar from '../components/Snackbar/UseSnackbar';
import {
    Button, DatePicker, Divider, Drawer, Input,
    InputNumber, Select, Switch, Table, Tag, Tooltip, message,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined, SaveOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCommonApiCall } from '../components/Api/commonApiCall';

// ─── Form defaults ────────────────────────────────────────────────────────────

const EMPTY_FORM = {
    id: null,
    name: "", surname: "", username: "", email: "", password: "",
    telephone: "", location: "", country: "", taxnumber: "",
    user_type: "", admin: false, approved: true, wati_msg_approval: true,
    wati_account: "", wati_token: "", wati_id: "",
    wati_bot: false, wati_cred_username: "", wati_cred_password: "",
    imapConfig: "", targetRange: [null, null], targetAmount: "",
};

function parseMD(raw) {
    try {
        return typeof raw === "string" ? JSON.parse(raw || "{}") : (raw || {});
    } catch { return {}; }
}

// ─── Form section heading ─────────────────────────────────────────────────────

function SectionHead({ title }) {
    return (
        <div style={{ fontWeight: 700, fontSize: 11, color: "#8c8c8c", textTransform: "uppercase", letterSpacing: 1, margin: "16px 0 10px" }}>
            {title}
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 3 }}>
                {label}{hint && <span style={{ color: "#bbb", marginLeft: 6 }}>{hint}</span>}
            </div>
            {children}
        </div>
    );
}

// ─── HRM page ─────────────────────────────────────────────────────────────────

function HRM() {
    const { CommonApiCall } = useCommonApiCall();
    const { authState } = useContext(AuthContext);
    const { SuccessSnackbar, ErrorSnackbar, ToastContainer } = useSnackbar();

    const [userSet, setUserSet]         = useState([]);
    const [userSetMap, setUserSetMap]   = useState({});
    const [usertypeSet, setUsertypeSet] = useState([]);
    const [searchTerm, setSearchTerm]   = useState("");
    const [supStatus, setSupStatus]     = useState("1");
    const [isLoading, setIsLoading]     = useState(true);

    // User drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [form, setForm]             = useState(EMPTY_FORM);
    const [saving, setSaving]         = useState(false);

    const setField = (key, value) => setForm(f => ({ ...f, [key]: value }));
    const isEdit = Boolean(form.id);

    // Settings (user types) drawer state
    const [settingsOpen, setSettingsOpen]   = useState(false);
    const [utypes, setUtypes]               = useState([]);
    const [utypesLoading, setUtypesLoading] = useState(false);
    const [utypeInput, setUtypeInput]       = useState("");
    const [utypeEditId, setUtypeEditId]     = useState(null);
    const [utypeSaving, setUtypeSaving]     = useState(false);

    // ── Data loading ──────────────────────────────────────────────────────────

    const headers = () => ({ accessToken: localStorage.getItem("accessToken") });

    const reloadUsers = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${API_DOMAIN}/auth/users/full`, { headers: headers() });
            if (res.data?.success) {
                const data = res.data.data ?? [];
                setUserSet(data);
                const map = {};
                data.forEach(u => { map[u.id] = u; });
                setUserSetMap(map);
            } else {
                message.error(res.data?.message || "Failed to load users");
            }
        } catch (e) {
            message.error(e.message);
        } finally {
            setIsLoading(false);
        }
    };

    const reloadUsertypes = () => {
        axios.post(API_DOMAIN + "/usertypes/get_user_types", { api_key: "uzdpart" })
            .then(res => { if (res.data.success) setUsertypeSet(res.data.data); })
            .catch(() => {});
    };

    // ── User types CRUD ───────────────────────────────────────────────────────

    const fetchUtypes = async () => {
        setUtypesLoading(true);
        try {
            const res = await axios.post(API_DOMAIN + "/usertypes/get_user_types", {},
                { headers: headers() });
            if (res.data?.success) setUtypes(res.data.data ?? []);
        } catch { message.error("Failed to load user types"); }
        finally { setUtypesLoading(false); }
    };

    const openSettings = () => {
        setSettingsOpen(true);
        fetchUtypes();
    };

    const saveUtype = async () => {
        if (!utypeInput.trim()) { message.error("Name is required"); return; }
        setUtypeSaving(true);
        try {
            const payload = utypeEditId
                ? { id: utypeEditId, user_type: utypeInput.trim() }
                : { user_type: utypeInput.trim() };
            const res = await axios.post(API_DOMAIN + "/usertypes/add_user_type", payload,
                { headers: headers() });
            if (res.data?.success) {
                message.success(utypeEditId ? "Updated" : "Created");
                setUtypeInput(""); setUtypeEditId(null);
                fetchUtypes(); reloadUsertypes();
            } else { message.error(res.data?.message || "Failed"); }
        } catch (e) { message.error(e.response?.data?.message || e.message); }
        finally { setUtypeSaving(false); }
    };

    const deleteUtype = async (id, name) => {
        if (!window.confirm(`Delete "${name}"?`)) return;
        try {
            const res = await axios.post(API_DOMAIN + "/usertypes/delete_user_type", { id },
                { headers: headers() });
            if (res.data?.success) { message.success("Deleted"); fetchUtypes(); reloadUsertypes(); }
            else message.error(res.data?.message || "Failed");
        } catch (e) { message.error(e.message); }
    };

    useEffect(() => {
        reloadUsers();
        reloadUsertypes();
    }, []); // eslint-disable-line

    // Client-side search + status filter
    const filteredUsers = useMemo(() => {
        let list = userSet;
        if (supStatus !== "all") {
            list = list.filter(u => String(u.approved) === supStatus);
        }
        if (searchTerm.trim()) {
            const q = searchTerm.toLowerCase();
            list = list.filter(u =>
                (u.name       || "").toLowerCase().includes(q) ||
                (u.surname    || "").toLowerCase().includes(q) ||
                (u.username   || "").toLowerCase().includes(q) ||
                (u.email      || "").toLowerCase().includes(q) ||
                (u.telephone  || "").toLowerCase().includes(q) ||
                (u.user_type  || "").toLowerCase().includes(q)
            );
        }
        return list;
    }, [userSet, supStatus, searchTerm]);

    // ── Drawer open/close ─────────────────────────────────────────────────────

    const openAdd = () => {
        setForm(EMPTY_FORM);
        setDrawerOpen(true);
    };

    const populateForm = (u) => {
        const md   = parseMD(u.moredetails);
        const cred = md.wati_cred || {};
        const tr   = md.targetRange;
        setForm({
            id:                u.id,
            name:              u.name              || "",
            surname:           u.surname           || "",
            username:          u.username          || "",
            email:             u.email             || "",
            password:          "",
            telephone:         u.telephone         || "",
            location:          u.location          || "",
            country:           u.country           || "",
            taxnumber:         u.taxnumber         || "",
            user_type:         u.user_type         || "",
            admin:             Boolean(Number(u.admin)),
            approved:          u.approved != null ? Boolean(Number(u.approved)) : true,
            wati_msg_approval: u.wati_msg_approval != null ? Boolean(Number(u.wati_msg_approval)) : true,
            wati_account:      u.wati_account      || "",
            wati_token:        u.wati_token        || "",
            wati_id:           u.wati_id           || "",
            wati_bot:          Boolean(md.wati_bot),
            wati_cred_username: cred.username      || "",
            wati_cred_password: cred.password      || "",
            imapConfig:        md.imapConfig ? JSON.stringify(md.imapConfig, null, 2) : "",
            targetRange:       Array.isArray(tr) && tr.length === 2
                                 ? [tr[0] ? dayjs(tr[0]) : null, tr[1] ? dayjs(tr[1]) : null]
                                 : [null, null],
            targetAmount:      md.targetAmount     || "",
        });
    };

    const openEdit = async (id) => {
        // Open immediately with cached data so the drawer feels instant
        const cached = userSetMap[id];
        if (cached) { populateForm(cached); setDrawerOpen(true); }
        // Then refresh from the full endpoint in the background
        try {
            const res = await axios.get(`${API_DOMAIN}/auth/users/full?id=${id}`, { headers: headers() });
            if (res.data?.success && res.data.data) {
                populateForm(res.data.data);
            }
        } catch { /* keep cached data */ }
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        reloadUsers(); // refresh list after save/close
    };

    // ── Save ──────────────────────────────────────────────────────────────────

    const handleSave = async () => {
        if (!form.username.trim()) { message.error("Username is required"); return; }
        if (!form.name.trim())     { message.error("Name is required");     return; }
        if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) {
            message.error("Valid email is required"); return;
        }
        if (!isEdit && !form.password) {
            message.error("Password is required for new users"); return;
        }
        if (form.admin && !isEdit) {
            if (!window.confirm("Make this user an admin?")) return;
        }

        // Parse imapConfig JSON
        let imapConfig = {};
        if (form.imapConfig.trim()) {
            try { imapConfig = JSON.parse(form.imapConfig); }
            catch { message.error("IMAP Config is not valid JSON"); return; }
        }

        const payload = {
            ...(isEdit ? { id: form.id } : {}),
            name:              form.name,
            surname:           form.surname     || null,
            username:          form.username,
            email:             form.email,
            telephone:         form.telephone,
            location:          form.location,
            country:           form.country,
            taxnumber:         form.taxnumber   || null,
            user_type:         form.user_type   || null,
            admin:             form.admin ? 1 : 0,
            approved:          form.approved ? 1 : 0,
            wati_msg_approval: form.wati_msg_approval ? 1 : 0,
            wati_account:      form.wati_account || null,
            wati_token:        form.wati_token   || null,
            wati_id:           form.wati_id      || null,
            wati_bot:          form.wati_bot,
            wati_cred_username: form.wati_cred_username || null,
            wati_cred_password: form.wati_cred_password || null,
            imapConfig,
            targetRange: form.targetRange[0] && form.targetRange[1]
                ? [form.targetRange[0].toISOString(), form.targetRange[1].toISOString()]
                : [],
            targetAmount: form.targetAmount || null,
        };
        if (form.password) payload.password = form.password;

        setSaving(true);
        try {
            const res = await axios.post(API_DOMAIN + "/auth/users/save", payload, {
                headers: { accessToken: localStorage.getItem("accessToken") },
            });
            if (res.data?.success) {
                message.success(res.data.message || (isEdit ? "User updated" : "User created"));
                closeDrawer();
            } else {
                message.error(res.data?.message || "Save failed");
            }
        } catch (e) {
            message.error(e.response?.data?.message || e.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────

    const handleDelete = async (user) => {
        if (!window.confirm(`Delete user ${user.username} (${user.name})?`)) return;
        try {
            const res = await CommonApiCall({ id: user.id }, "auth/delete_user", "post");
            if (res.status) {
                setUserSet(res.data);
                SuccessSnackbar("Deleted successfully");
            } else {
                ErrorSnackbar(res.message);
            }
        } catch (e) {
            ErrorSnackbar(e.message);
        }
    };

    // ── Table columns ─────────────────────────────────────────────────────────

    const columns = [
        {
            title: "#",
            key: "index",
            width: 45,
            render: (_, __, i) => <span style={{ color: "#bfbfbf", fontSize: 12 }}>{i + 1}</span>,
        },
        {
            title: "Name",
            key: "name",
            render: (_, u) => (
                <div>
                    <div style={{ fontWeight: 600 }}>
                        {u.name}{u.surname ? ` ${u.surname}` : ""}
                    </div>
                    <div style={{ fontSize: 11, color: "#8c8c8c" }}>{u.email}</div>
                </div>
            ),
        },
        {
            title: "Username",
            dataIndex: "username",
            key: "username",
            render: v => <span style={{ fontSize: 12, fontFamily: "monospace" }}>{v}</span>,
        },
        {
            title: "Contact",
            key: "contact",
            render: (_, u) => (
                <div style={{ fontSize: 12 }}>
                    <div>{u.telephone || "—"}</div>
                    <div style={{ color: "#8c8c8c" }}>{u.location}{u.country ? `, ${u.country}` : ""}</div>
                </div>
            ),
        },
        {
            title: "Role",
            key: "role",
            render: (_, u) => (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {u.user_type && <Tag style={{ margin: 0, fontSize: 11 }}>{u.user_type}</Tag>}
                    {Boolean(Number(u.admin)) && <Tag color="red" style={{ margin: 0, fontSize: 11 }}>Admin</Tag>}
                    {!Boolean(Number(u.approved)) && <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>Unapproved</Tag>}
                </div>
            ),
        },
        {
            title: "WATI",
            key: "wati",
            render: (_, u) => {
                const md = parseMD(u.moredetails);
                return (
                    <div style={{ fontSize: 12 }}>
                        <div>{u.wati_token
                            ? <Tag color="green" style={{ margin: 0, fontSize: 11 }}>Token ✓</Tag>
                            : <Tag color="default" style={{ margin: 0, fontSize: 11 }}>No token</Tag>}
                        </div>
                        {md.wati_bot && <Tag color="blue" style={{ margin: "4px 0 0", fontSize: 11 }}>Bot</Tag>}
                    </div>
                );
            },
        },
        {
            title: "Target",
            key: "target",
            render: (_, u) => {
                const md = parseMD(u.moredetails);
                if (!md.targetAmount && !md.targetRange?.[0]) return <span style={{ color: "#d9d9d9" }}>—</span>;
                return (
                    <div style={{ fontSize: 12 }}>
                        {md.targetAmount && (
                            <div style={{ fontWeight: 600 }}>
                                AED {Number(md.targetAmount).toLocaleString()}
                            </div>
                        )}
                        {Array.isArray(md.targetRange) && md.targetRange[0] && (
                            <div style={{ color: "#8c8c8c", fontSize: 11 }}>
                                {dayjs(md.targetRange[0]).format("MMM YY")} – {dayjs(md.targetRange[1]).format("MMM YY")}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: "",
            key: "actions",
            width: 80,
            render: (_, u) => (
                <div style={{ display: "flex", gap: 6 }}>
                    <Tooltip title="Edit">
                        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(u.id)} />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(u)} />
                    </Tooltip>
                </div>
            ),
        },
    ];

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div>
            {/* Top bar */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <Input.Search
                    placeholder="Name, username, email, phone…"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onSearch={() => {}}
                    allowClear
                    onClear={() => setSearchTerm("")}
                    style={{ width: 240, flex: "none" }}
                />
                <div style={{ width: 90, flexShrink: 0 }}>
                    <Select
                        value={supStatus}
                        onChange={setSupStatus}
                        style={{ width: "100%" }}
                        options={[
                            { value: "all", label: "All" },
                            { value: "1",   label: "Active" },
                            { value: "0",   label: "Inactive" },
                        ]}
                    />
                </div>
                <Button icon={<ReloadOutlined />} onClick={reloadUsers} loading={isLoading} />
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                    {filteredUsers.length}{filteredUsers.length !== userSet.length ? ` / ${userSet.length}` : ""} user{userSet.length !== 1 ? "s" : ""}
                </span>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} style={{ marginLeft: "auto" }}>Add User</Button>
                <Button icon={<SettingOutlined />} onClick={openSettings} />
            </div>

            {/* Table */}
            <Table
                dataSource={filteredUsers}
                columns={columns}
                rowKey="id"
                loading={isLoading}
                size="middle"
                pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
                style={{ background: "#fff", borderRadius: 8 }}
            />

            {/* ── User Drawer ── */}
            <Drawer
                title={isEdit ? `Edit User · ${form.name || form.username}` : "Add New User"}
                open={drawerOpen}
                onClose={closeDrawer}
                width={480}
                styles={{ body: { padding: "12px 24px 80px" } }}
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <Button onClick={closeDrawer}>Cancel</Button>
                        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
                            {isEdit ? "Update User" : "Create User"}
                        </Button>
                    </div>
                }
            >
                {/* ── Identity ── */}
                <SectionHead title="Identity" />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                    <Field label="First Name *">
                        <Input value={form.name} onChange={e => setField("name", e.target.value)} placeholder="Name" />
                    </Field>
                    <Field label="Surname">
                        <Input value={form.surname} onChange={e => setField("surname", e.target.value)} placeholder="Surname" />
                    </Field>
                </div>
                <Field label="Username / Login *">
                    <Input value={form.username} onChange={e => setField("username", e.target.value)} placeholder="username@company.com" />
                </Field>
                <Field label="Email *">
                    <Input type="email" value={form.email} onChange={e => setField("email", e.target.value)} placeholder="email@company.com" />
                </Field>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                    <Field label="Telephone">
                        <Tooltip title="Include country code, no + (e.g. 971501234567)">
                            <Input value={form.telephone} onChange={e => setField("telephone", e.target.value)} placeholder="971xxxxxxxxx" />
                        </Tooltip>
                    </Field>
                    <Field label="Tax Number">
                        <Input value={form.taxnumber} onChange={e => setField("taxnumber", e.target.value)} placeholder="Tax / ID number" />
                    </Field>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                    <Field label="Location">
                        <Input value={form.location} onChange={e => setField("location", e.target.value)} placeholder="e.g. UAE" />
                    </Field>
                    <Field label="Country">
                        <Input value={form.country} onChange={e => setField("country", e.target.value)} placeholder="e.g. India" />
                    </Field>
                </div>

                <Divider style={{ margin: "8px 0" }} />

                {/* ── Password ── */}
                <SectionHead title="Password" />
                <Field label={isEdit ? "New Password" : "Password *"} hint={isEdit ? "(leave blank to keep current)" : undefined}>
                    <Input.Password
                        value={form.password}
                        onChange={e => setField("password", e.target.value)}
                        placeholder={isEdit ? "Leave blank — no change" : "Min 6 characters"}
                    />
                </Field>

                <Divider style={{ margin: "8px 0" }} />

                {/* ── Permissions ── */}
                <SectionHead title="Permissions & Role" />
                <Field label="User Type">
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Select user type"
                        value={form.user_type || null}
                        onChange={v => setField("user_type", v)}
                        allowClear
                        options={usertypeSet.map(t => ({ label: t.user_type, value: t.user_type }))}
                    />
                </Field>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 10 }}>
                    <Field label="Admin">
                        <Switch checked={form.admin} onChange={v => setField("admin", v)} />
                    </Field>
                    <Field label="Approved">
                        <Switch checked={form.approved} onChange={v => setField("approved", v)} />
                    </Field>
                    <Field label="WATI Msg Approval">
                        <Switch checked={form.wati_msg_approval} onChange={v => setField("wati_msg_approval", v)} />
                    </Field>
                </div>

                <Divider style={{ margin: "8px 0" }} />

                {/* ── WATI ── */}
                <SectionHead title="WATI Configuration" />
                <Field label="WATI Account">
                    <Input value={form.wati_account} onChange={e => setField("wati_account", e.target.value)} placeholder="account@company.com" />
                </Field>
                <Field label="WATI ID">
                    <Input value={form.wati_id} onChange={e => setField("wati_id", e.target.value)} placeholder="662dedd72555105dee191623" />
                </Field>
                <Field label="WATI Token">
                    <Input.TextArea
                        value={form.wati_token}
                        onChange={e => setField("wati_token", e.target.value)}
                        placeholder="Paste WATI bearer token…"
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        style={{ fontFamily: "monospace", fontSize: 11 }}
                    />
                </Field>
                <Field label="WATI Bot">
                    <Switch checked={form.wati_bot} onChange={v => setField("wati_bot", v)} />
                </Field>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                    <Field label="WATI Cred Username">
                        <Input value={form.wati_cred_username} onChange={e => setField("wati_cred_username", e.target.value)} placeholder="wati login email" />
                    </Field>
                    <Field label="WATI Cred Password">
                        <Input.Password value={form.wati_cred_password} onChange={e => setField("wati_cred_password", e.target.value)} placeholder="wati password" />
                    </Field>
                </div>

                <Divider style={{ margin: "8px 0" }} />

                {/* ── Targets ── */}
                <SectionHead title="Sales Target" />
                <Field label="Target Period">
                    <DatePicker.RangePicker
                        value={form.targetRange}
                        onChange={v => setField("targetRange", v || [null, null])}
                        format="DD MMM YYYY"
                        style={{ width: "100%" }}
                    />
                </Field>
                <Field label="Target Amount (AED)">
                    <InputNumber
                        style={{ width: "100%" }}
                        value={form.targetAmount}
                        onChange={v => setField("targetAmount", v != null ? String(v) : "")}
                        placeholder="e.g. 50000"
                        min={0}
                        controls={false}
                        formatter={v => v ? Number(v).toLocaleString() : ""}
                        parser={v => v ? v.replace(/,/g, "") : ""}
                    />
                </Field>

                <Divider style={{ margin: "8px 0" }} />

                {/* ── IMAP Config ── */}
                <SectionHead title="IMAP Config" />
                <Field label="JSON Config" hint="(paste raw JSON)">
                    <Input.TextArea
                        value={form.imapConfig}
                        onChange={e => setField("imapConfig", e.target.value)}
                        placeholder={'{\n  "host": "imap.gmail.com",\n  "port": 993,\n  …\n}'}
                        autoSize={{ minRows: 4, maxRows: 10 }}
                        style={{ fontFamily: "monospace", fontSize: 11 }}
                    />
                </Field>
            </Drawer>

            {/* ── Settings: User Types ── */}
            <Drawer
                title="Settings · User Types"
                open={settingsOpen}
                onClose={() => { setSettingsOpen(false); setUtypeInput(""); setUtypeEditId(null); }}
                width={380}
                styles={{ body: { padding: "16px 24px" } }}
            >
                {/* Add / edit input */}
                <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                    <Input
                        value={utypeInput}
                        onChange={e => setUtypeInput(e.target.value)}
                        placeholder="e.g. Sales Executive"
                        onPressEnter={saveUtype}
                        style={{ flex: 1 }}
                        suffix={utypeEditId ? (
                            <span
                                style={{ fontSize: 11, color: "#bbb", cursor: "pointer" }}
                                onClick={() => { setUtypeInput(""); setUtypeEditId(null); }}
                            >✕ cancel edit</span>
                        ) : null}
                    />
                    <Button
                        type="primary"
                        loading={utypeSaving}
                        onClick={saveUtype}
                        icon={utypeEditId ? <SaveOutlined /> : <PlusOutlined />}
                    >
                        {utypeEditId ? "Update" : "Add"}
                    </Button>
                </div>

                <Divider style={{ margin: "0 0 12px" }} />

                {/* List */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {utypesLoading
                        ? <div style={{ textAlign: "center", padding: 24 }}><ReloadOutlined spin /></div>
                        : utypes.length === 0
                            ? <div style={{ color: "#bfbfbf", textAlign: "center", padding: 24 }}>No user types yet</div>
                            : utypes.map(t => (
                                <div key={t.id} style={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    padding: "8px 12px", borderRadius: 6,
                                    background: utypeEditId === t.id ? "#fffbe6" : "#fafafa",
                                    border: `1px solid ${utypeEditId === t.id ? "#ffe58f" : "#f0f0f0"}`,
                                }}>
                                    <span style={{ fontSize: 13, fontWeight: 500 }}>{t.user_type}</span>
                                    <div style={{ display: "flex", gap: 6 }}>
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => { setUtypeEditId(t.id); setUtypeInput(t.user_type); }}
                                        />
                                        <Button
                                            size="small"
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={() => deleteUtype(t.id, t.user_type)}
                                        />
                                    </div>
                                </div>
                            ))
                    }
                </div>
            </Drawer>

            <ToastContainer />
        </div>
    );
}

export default HRM;
